import { RangeHorizon } from '@toggle/chart';
import { SkeletonLoader } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useCompanyCompetitors } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '~/routes/app-paths';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import {
  getChartSubPageSearchParams,
  SnakeTypes,
} from '~/widgets/chart-peer-group/utils/chart-subpage-navigation/chart-subpage-navigation';

import { MultiLineChartModule } from '../multi-line-chart/MultiLineChartModule';
import styles from './OverviewFinancials.module.scss';

export interface OverviewFinancialsProps {
  entity: Entity;
  isDrawer?: boolean;
}

const COMPETITORS_AMOUNT = 4;

export const OverviewFinancials = ({
  entity,
  isDrawer = false,
}: OverviewFinancialsProps) => {
  const { t } = useTranslation('asset-overview');
  const [searchParams] = useSearchParams();

  const { competitorsData, isFetching, isError, error } = useCompanyCompetitors(
    {
      entityTag: entity.tag,
      limit: COMPETITORS_AMOUNT,
    }
  );

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '350px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (isError) {
    if (error?.status === 404) {
      return null;
    }

    return <ErrorMessageWrapper displayIcon />;
  }

  const competitorEntities = competitorsData.map(({ entity }) => entity);
  const entities = [entity, ...competitorEntities];

  const chartSubPageSearchParams = getChartSubPageSearchParams({
    entityTag: entity.tag,
    prevSearchParams: searchParams,
    horizon: RangeHorizon.OneYear,
    snakeType: SnakeTypes.Price,
  }).toString();

  const linkToChartSubPage = `${appPaths.assetOverview}?${chartSubPageSearchParams}`;

  return (
    <div
      className={clsx(styles.container, { [styles.isDrawer]: isDrawer })}
    >
      <SectionTitle
        title={t('asset-overview:performance')}
        linkTo={linkToChartSubPage}
      />

      <p className={styles.title}>
        {t('asset-overview:last12Months')}
        <span>%</span>
      </p>

      <MultiLineChartModule
        entities={entities}
        chartConfig={{
          enableZoom: false,
        }}
        tooltipSortConfig={{ field: 'priceFormatted', desc: true }}
        source={
          isDrawer
            ? POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW_DRAWER
            : POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW
        }
      />
    </div>
  );
};
